import React, { FC, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  AUDIT_EVENT_FIELDS,
  IEntitlementsSyncProperties,
  ISubscriptionAuditEvent,
} from '../types/audit-event';
import { GridTooltip } from './General/GridTooltip';
import { ErrorOverlayDataGrid } from './ErrorOverlayDataGrid';
import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import UnsubscribeOutlinedIcon from '@mui/icons-material/UnsubscribeOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { EntitlementsSyncDetailDialog } from './EntitlementsSyncDetailDialog';

interface AuditEventsDataGridProps {
  auditEvents: ISubscriptionAuditEvent[];
  loading?: boolean;
  error?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
}

const formatDateToDDMMYYYY = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const EVENT_TYPE_CONFIG: Record<
  string,
  { icon: React.ReactElement; label: string; color: 'success' | 'error' | 'info' | 'warning' }
> = {
  'auditing.event.subscription.roleassignment.created.v1': {
    icon: <PersonAddAlt1OutlinedIcon fontSize="small" />,
    label: 'Role assignment created',
    color: 'success',
  },
  'auditing.event.subscription.roleassignment.removed.v1': {
    icon: <PersonRemoveOutlinedIcon fontSize="small" />,
    label: 'Role assignment removed',
    color: 'error',
  },
  'auditing.event.subscription.invite.created.v1': {
    icon: <ForwardToInboxOutlinedIcon fontSize="small" />,
    label: 'Invite created',
    color: 'info',
  },
  'auditing.event.subscription.invite.removed.v1': {
    icon: <UnsubscribeOutlinedIcon fontSize="small" />,
    label: 'Invite removed',
    color: 'warning',
  },
  'auditing.event.subscription.entitlements.pushed.roleassignment.v1': {
    icon: <PublishOutlinedIcon fontSize="small" />,
    label: 'Entitlement push (role assignment)',
    color: 'info',
  },
  'auditing.event.subscription.entitlements.pushed.roledefinitionupdate.v1': {
    icon: <PublishOutlinedIcon fontSize="small" />,
    label: 'Entitlements pushed (role definition update)',
    color: 'info',
  },
  'auditing.event.subscription.entitlements.deleted.roleassignment.v1': {
    icon: <PublishOutlinedIcon fontSize="small" />,
    label: 'Entitlement delete pushed (role assignment removal)',
    color: 'error',
  },
  'auditing.event.subscription.entitlements.synced.v1': {
    icon: <SyncOutlinedIcon fontSize="small" />,
    label: 'Entitlements synced',
    color: 'info',
  },
};

type ChipColor = 'success' | 'error' | 'info' | 'warning';

// Entitlement syncs: warning on any failure, success once every identity is synced.
const syncChipColor = (
  properties: IEntitlementsSyncProperties | null | undefined,
  fallback: ChipColor
): ChipColor => {
  if (!properties) return fallback;
  if (properties.failures.length > 0) return 'warning';
  const synced = properties.pushedCount + properties.deletedCount;
  return synced >= properties.expectedCount ? 'success' : fallback;
};

const ACTOR_TYPE_CONFIG: Record<string, { icon: React.ReactElement; label: string }> = {
  'auditing.event.actor.user.v1': {
    icon: <PersonOutlinedIcon fontSize="small" />,
    label: 'User',
  },
  'auditing.event.actor.system.v1': {
    icon: <SmartToyOutlinedIcon fontSize="small" />,
    label: 'System',
  },
  'auditing.event.actor.customersupport.v1': {
    icon: <SupportAgentOutlinedIcon fontSize="small" />,
    label: 'Customer support',
  },
};

const columns: GridColDef[] = [
  {
    field: AUDIT_EVENT_FIELDS.DATE_OCCURRED,
    headerName: 'Date',
    minWidth: 160,
    flex: 1,
    type: 'dateTime',
    valueGetter: (_value, row) => (row.dateOccurred ? new Date(row.dateOccurred) : null),
    renderCell: (params: GridRenderCellParams) => (
      <GridTooltip params={{ ...params, value: formatDateToDDMMYYYY(params.row.dateOccurred) }} />
    ),
  },
  {
    field: AUDIT_EVENT_FIELDS.TYPE,
    headerName: 'Event type',
    minWidth: 230,
    flex: 1,
    renderCell: (params: GridRenderCellParams) => {
      const config = EVENT_TYPE_CONFIG[params.value];
      if (!config) return <GridTooltip params={params} />;
      return (
        <Tooltip title={params.value}>
          <Chip
            icon={config.icon}
            label={config.label}
            color={syncChipColor(params.row.properties, config.color)}
            size="small"
            variant="filled"
          />
        </Tooltip>
      );
    },
  },
  {
    field: AUDIT_EVENT_FIELDS.DESCRIPTION,
    headerName: 'Description',
    minWidth: 250,
    flex: 2,
    renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
  },
  {
    field: AUDIT_EVENT_FIELDS.PERFORMED_BY,
    headerName: 'Performed by',
    minWidth: 220,
    flex: 1,
    valueGetter: (_value, row) =>
      row.performedBy?.email ?? row.performedBy?.name ?? row.performedBy?.id ?? '-',
    renderCell: (params: GridRenderCellParams) => {
      const actorType = params.row.performedBy?.type;
      const actorConfig = ACTOR_TYPE_CONFIG[actorType];
      if (!actorConfig) return <GridTooltip params={params} />;
      return (
        <Tooltip title={`${actorConfig.label}: ${params.value}`}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ height: '100%', overflow: 'hidden' }}>
            {actorConfig.icon}
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          </Stack>
        </Tooltip>
      );
    },
  },
  {
    field: AUDIT_EVENT_FIELDS.CORRELATION_ID,
    headerName: 'Correlation ID',
    minWidth: 280,
    flex: 1,
    renderCell: (params: GridRenderCellParams) => {
      const value: string | null = params.value;
      return (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
          <Tooltip title={value ?? ''}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: 'text.secondary',
                flex: 1,
                minWidth: 0,
              }}>
              {value ?? '-'}
            </Typography>
          </Tooltip>
          {value && (
            <Tooltip title="Copy">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(value);
                }}
                sx={{ flexShrink: 0, ml: 0.5 }}>
                <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      );
    },
  },
];

const AuditEventsDataGrid: FC<AuditEventsDataGridProps> = ({
  auditEvents,
  loading = false,
  error = false,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const [detailEvent, setDetailEvent] = useState<ISubscriptionAuditEvent | null>(null);

  if (error) {
    return <ErrorOverlayDataGrid />;
  }

  const columnsWithDetails: GridColDef[] = [
    ...columns,
    {
      field: 'actions',
      headerName: '',
      width: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) =>
        params.row.properties ? (
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDetailEvent(params.row as ISubscriptionAuditEvent);
              }}>
              <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <>
      <DataGrid
        autoHeight
        columns={columnsWithDetails}
        getRowId={(row) => row.id}
        loading={loading}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
          if (newPageSize !== pageSize) {
            onPageSizeChange(newPageSize);
          } else {
            onPageChange(newPage);
          }
        }}
        pageSizeOptions={[5, 10, 20]}
        rows={auditEvents || []}
        disableRowSelectionOnClick
        checkboxSelection={false}
        rowHeight={52}
        initialState={{
          sorting: {
            sortModel: [{ field: AUDIT_EVENT_FIELDS.DATE_OCCURRED, sort: 'desc' }],
          },
        }}
        sx={{
          backgroundColor: 'white',
        }}
      />
      <EntitlementsSyncDetailDialog event={detailEvent} onClose={() => setDetailEvent(null)} />
    </>
  );
};

export default AuditEventsDataGrid;
